import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRealtimeInbox } from "../../hooks/useRealtimeInbox";

import {
  Search,
  Send,
  User,
  Plus,
  Radio,
  QrCode,
  ShieldCheck,
  Check,
  CheckCheck,
  Clock,
  Zap,
  Tag as TagIcon,
  Paperclip,
  RefreshCw,
  Smartphone,
  ChevronRight,
  AlertCircle,
} from "lucide-react";
import {
  listConversations,
  createConversation,
  getConversationMessages,
  sendMessage,
  markConversationRead,
  listCannedReplies,
  listQRInstances,
  listChannels,
  type CurrentUser,
  type Conversation,
  type Message,
  type CannedReply,
} from "../../lib/api";

const COUNTRY_CODES = [
  { code: "+91", country: "IN", flag: "🇮🇳", name: "India (+91)" },
  { code: "+1", country: "US", flag: "🇺🇸", name: "USA / Canada (+1)" },
  { code: "+44", country: "GB", flag: "🇬🇧", name: "UK (+44)" },
  { code: "+971", country: "AE", flag: "🇦🇪", name: "UAE (+971)" },
  { code: "+966", country: "SA", flag: "🇸🇦", name: "Saudi Arabia (+966)" },
  { code: "+65", country: "SG", flag: "🇸🇬", name: "Singapore (+65)" },
  { code: "+61", country: "AU", flag: "🇦🇺", name: "Australia (+61)" },
  { code: "+49", country: "DE", flag: "🇩🇪", name: "Germany (+49)" },
  { code: "+33", country: "FR", flag: "🇫🇷", name: "France (+33)" },
  { code: "+880", country: "BD", flag: "🇧🇩", name: "Bangladesh (+880)" },
  { code: "+92", country: "PK", flag: "🇵🇰", name: "Pakistan (+92)" },
  { code: "+977", country: "NP", flag: "🇳🇵", name: "Nepal (+977)" },
  { code: "+94", country: "LK", flag: "🇱🇰", name: "Sri Lanka (+94)" },
  { code: "+60", country: "MY", flag: "🇲🇾", name: "Malaysia (+60)" },
  { code: "+62", country: "ID", flag: "🇮🇩", name: "Indonesia (+62)" },
  { code: "+55", country: "BR", flag: "🇧🇷", name: "Brazil (+55)" },
  { code: "+27", country: "ZA", flag: "🇿🇦", name: "South Africa (+27)" },
  { code: "+234", country: "NG", flag: "🇳🇬", name: "Nigeria (+234)" },
  { code: "+254", country: "KE", flag: "🇰🇪", name: "Kenya (+254)" },
];

function formatPhoneWithCountryCode(raw: string, countryCode: string = "+91"): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("+")) return trimmed;
  const digits = trimmed.replace(/\D/g, "");
  const ccDigits = countryCode.replace(/\D/g, "");
  if (digits.startsWith(ccDigits) && digits.length > ccDigits.length + 6) {
    return `+${digits}`;
  }
  return `${countryCode}${digits}`;
}

export function UnifiedInboxView({ token, user }: { token: string; user: CurrentUser }) {
  const queryClient = useQueryClient();
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [selectedChannelId, setSelectedChannelId] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>("+91");
  const [messageInput, setMessageInput] = useState("");
  const [isNewChatOpen, setIsNewChatOpen] = useState(false);
  const [newChatPhone, setNewChatPhone] = useState("");
  const [newChatName, setNewChatName] = useState("");
  const [newChatChannel, setNewChatChannel] = useState<"qr_whatsapp" | "meta_official">("qr_whatsapp");
  const [newChatInitialMessage, setNewChatInitialMessage] = useState("");
  const [showCannedReplies, setShowCannedReplies] = useState(false);

  const { isConnected: isRealtimeConnected } = useRealtimeInbox({
    token,
    activeConversationId,
    tenantId: user.tenant_id,
  });

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Live QR Instances & Channels Query for top selector
  const qrInstancesQuery = useQuery({
    queryKey: ["qr-instances", user.tenant_id],
    queryFn: () => listQRInstances(token, user.tenant_id),
    refetchInterval: isRealtimeConnected ? 15_000 : 5_000,
    refetchIntervalInBackground: true,
  });

  const channelsQuery = useQuery({
    queryKey: ["channels", user.tenant_id],
    queryFn: () => listChannels(token, undefined, user.tenant_id),
    refetchInterval: isRealtimeConnected ? 15_000 : 5_000,
    refetchIntervalInBackground: true,
  });

  const qrInstances = qrInstancesQuery.data || [];
  const metaChannels = channelsQuery.data || [];

  // Live Conversations Query (Instant via Redis Pub/Sub, with 10s fallback)
  const conversationsQuery = useQuery({
    queryKey: ["inbox", "conversations", filterStatus, user.tenant_id],
    queryFn: () => listConversations(token, filterStatus === "all" ? undefined : filterStatus, user.tenant_id),
    refetchInterval: isRealtimeConnected ? 10_000 : 2_000,
    refetchIntervalInBackground: true,
  });

  const conversations = conversationsQuery.data || [];

  // Automatically select first conversation if none selected
  useEffect(() => {
    if (!activeConversationId && conversations.length > 0) {
      const first = conversations[0];
      if (first) {
        setActiveConversationId(first.id);
      }
    }
  }, [conversations, activeConversationId]);

  // Live Messages Query for Active Conversation (Instant via Redis Pub/Sub, with 10s fallback)
  const messagesQuery = useQuery({
    queryKey: ["inbox", "messages", activeConversationId],
    queryFn: () => (activeConversationId ? getConversationMessages(token, activeConversationId) : []),
    enabled: !!activeConversationId,
    refetchInterval: isRealtimeConnected ? 10_000 : 1_000,
    refetchIntervalInBackground: true,
  });


  const messages = messagesQuery.data || [];

  // Live Canned Replies
  const cannedRepliesQuery = useQuery({
    queryKey: ["inbox", "canned-replies", user.tenant_id],
    queryFn: () => listCannedReplies(token, user.tenant_id),
  });

  const cannedReplies = cannedRepliesQuery.data || [];

  // Auto-scroll messages inside its container only (does not scroll window or sidebar)
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages.length, activeConversationId]);

  // Send Message Mutation
  const sendMutation = useMutation({
    mutationFn: (text: string) =>
      sendMessage(token, activeConversationId!, {
        message_type: "text",
        content: { text: text.trim() },
      }),
    onSuccess: () => {
      setMessageInput("");
      queryClient.invalidateQueries({ queryKey: ["inbox", "messages", activeConversationId] });
      queryClient.invalidateQueries({ queryKey: ["inbox", "conversations"] });
    },
    onError: (err: any) => {
      alert(`Message failed to send: ${err?.message || "Unknown error"}`);
    },
  });

  // Start New Conversation Mutation
  const newChatMutation = useMutation({
    mutationFn: () =>
      createConversation(
        token,
        {
          phone_e164: formatPhoneWithCountryCode(newChatPhone, selectedCountryCode),
          contact_name: newChatName.trim() || undefined,
          channel_type: newChatChannel,
          initial_message: newChatInitialMessage.trim() || undefined,
        },
        user.tenant_id
      ),
    onSuccess: (newConv) => {
      queryClient.invalidateQueries({ queryKey: ["inbox", "conversations"] });
      setActiveConversationId(newConv.id);
      setIsNewChatOpen(false);
      setNewChatPhone("");
      setNewChatName("");
      setNewChatInitialMessage("");
    },
    onError: (err: any) => {
      alert(err?.message || "Failed to start chat. Please check recipient phone number.");
    },
  });

  const activeConversation = conversations.find((c) => c.id === activeConversationId);

  const filteredConversations = conversations.filter((c) => {
    const contactName = c.contact?.first_name || "";
    const contactPhone = c.contact?.phone_e164 || "";
    const preview = c.last_message_preview || "";
    const q = searchQuery.toLowerCase().trim();
    const formattedSearch = formatPhoneWithCountryCode(searchQuery, selectedCountryCode);
    const matchesSearch =
      !q ||
      contactName.toLowerCase().includes(q) ||
      contactPhone.toLowerCase().includes(q) ||
      contactPhone.includes(formattedSearch) ||
      preview.toLowerCase().includes(q);
    if (!matchesSearch) return false;

    if (selectedChannelId === "all") return true;
    if (selectedChannelId.startsWith("qr_")) {
      return c.channel_type === "qr_whatsapp";
    }
    if (selectedChannelId.startsWith("meta_")) {
      return c.channel_type === "meta_official";
    }
    return true;
  });

  function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!messageInput.trim() || !activeConversationId || sendMutation.isPending) return;
    sendMutation.mutate(messageInput);
  }

  function insertCannedReply(reply: CannedReply) {
    setMessageInput(reply.content);
    setShowCannedReplies(false);
  }

  const isSearchPhoneNumber = searchQuery.replace(/[^\d]/g, "").length >= 4;
  const searchFormattedPhone = formatPhoneWithCountryCode(searchQuery, selectedCountryCode);

  function handleStartChatFromSearch() {
    setNewChatPhone(searchQuery.trim());
    if (selectedChannelId.startsWith("meta_")) {
      setNewChatChannel("meta_official");
    } else {
      setNewChatChannel("qr_whatsapp");
    }
    setIsNewChatOpen(true);
  }

  const activeQR = qrInstances.find((i) => `qr_${i.id}` === selectedChannelId);
  const activeMeta = metaChannels.find((c) => `meta_${c.id}` === selectedChannelId);

  return (
    <div className="h-[calc(100vh-130px)] flex flex-col space-y-3">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Radio className="w-6 h-6 text-emerald-600" />
            Live Omnichannel Inbox
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              Live Real-Time
            </span>
          </h1>
          <p className="text-xs text-slate-500">
            Real-time chat with Meta Verified WABA and Direct QR Multi-Device WhatsApp sessions.
          </p>
        </div>

        {/* Top Channel Selector & Actions */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Active Sending Channel Switcher */}
          <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-2xs">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1 shrink-0">
              <Smartphone className="w-3.5 h-3.5 text-emerald-600" />
              Channel:
            </span>
            <select
              value={selectedChannelId}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedChannelId(val);
                if (val.startsWith("meta_")) {
                  setNewChatChannel("meta_official");
                } else {
                  setNewChatChannel("qr_whatsapp");
                }
              }}
              className="text-xs font-semibold text-slate-800 bg-transparent border-none outline-none cursor-pointer pr-1"
            >
              <option value="all">🌐 All Channels &amp; Numbers (Unified)</option>
              {qrInstances.length > 0 && (
                <optgroup label="📱 QR WhatsApp Instances">
                  {qrInstances.map((inst) => (
                    <option key={inst.id} value={`qr_${inst.id}`}>
                      📱 {inst.name} ({inst.phone_number ? `+${inst.phone_number}` : "No number"}) {inst.status === "connected" ? "🟢 [Connected]" : "⚪ [Offline]"}
                    </option>
                  ))}
                </optgroup>
              )}
              {metaChannels.length > 0 && (
                <optgroup label="🛡️ Meta Official WABA Channels">
                  {metaChannels.map((ch) => (
                    <option key={ch.id} value={`meta_${ch.id}`}>
                      🛡️ {ch.name} ({ch.phone_number}) {ch.status === "active" ? "🟢 [Active]" : "⚪ [Offline]"}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          <button
            onClick={() => {
              if (selectedChannelId.startsWith("meta_")) {
                setNewChatChannel("meta_official");
              } else {
                setNewChatChannel("qr_whatsapp");
              }
              setIsNewChatOpen(true);
            }}
            className="btn-primary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>New Chat</span>
          </button>
        </div>
      </div>

      {/* Main 3-Column Layout */}
      <div className="flex-1 min-h-0 grid grid-cols-12 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
        
        {/* Left Column: Thread List (4 Cols) */}
        <div className="col-span-12 md:col-span-4 lg:col-span-4 border-r border-slate-200 flex flex-col h-full min-h-0 bg-slate-50/50">

          
          {/* Search & Filter */}
          <div className="p-3 border-b border-slate-200 space-y-2 bg-white">
            <div className="flex items-center gap-1.5">
              <select
                value={selectedCountryCode}
                onChange={(e) => setSelectedCountryCode(e.target.value)}
                className="h-8 pl-1.5 pr-1 rounded-lg border border-slate-200 text-[11px] bg-slate-100 font-mono font-semibold focus:outline-none focus:border-emerald-600 shrink-0 cursor-pointer"
                title="Default Country Code Prefix"
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.code}
                  </option>
                ))}
              </select>
              <div className="relative flex-1">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                <input
                  type="text"
                  placeholder="Search name or enter phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && searchQuery.trim()) {
                      handleStartChatFromSearch();
                    }
                  }}
                  className="w-full pl-7 pr-2 py-1.5 rounded-lg border border-slate-200 text-xs bg-slate-50 focus:outline-none focus:border-emerald-600"
                />
              </div>
            </div>

            <div className="flex items-center gap-1 text-xs">
              <button
                onClick={() => setFilterStatus("all")}
                className={`px-2 py-1 rounded-md font-semibold text-[11px] ${
                  filterStatus === "all" ? "bg-emerald-50 text-emerald-700 font-bold" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                All ({conversations.length})
              </button>
              <button
                onClick={() => setFilterStatus("open")}
                className={`px-2 py-1 rounded-md font-semibold text-[11px] ${
                  filterStatus === "open" ? "bg-emerald-50 text-emerald-700 font-bold" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                Open
              </button>
              <button
                onClick={() => setFilterStatus("unread")}
                className={`px-2 py-1 rounded-md font-semibold text-[11px] ${
                  filterStatus === "unread" ? "bg-emerald-50 text-emerald-700 font-bold" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                Unread
              </button>
            </div>
          </div>

          {/* Thread Items List */}
          <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {/* Direct Start Chat from Search Query */}
            {isSearchPhoneNumber && (
              <button
                onClick={handleStartChatFromSearch}
                className="w-full p-3 text-left flex items-center gap-3 bg-emerald-50/80 hover:bg-emerald-100 border-b border-emerald-200 transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-xs shrink-0 shadow-2xs">
                  <Plus className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-bold text-emerald-950 text-xs">Start Direct Chat</span>
                    <span className="text-[10px] px-1.5 py-0.2 rounded bg-emerald-200 text-emerald-800 font-bold">New</span>
                  </div>
                  <p className="text-[11px] text-emerald-700 font-mono truncate">
                    Direct message: {searchFormattedPhone}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-emerald-600" />
              </button>
            )}

            {conversationsQuery.isLoading ? (
              <div className="p-8 text-center text-xs text-slate-400 flex flex-col items-center justify-center gap-2">
                <RefreshCw className="w-5 h-5 text-emerald-600 animate-spin" />
                <span>Loading conversations...</span>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-4 text-center text-xs text-slate-400 space-y-3">
                {searchQuery.trim() ? (
                  <div className="p-4 bg-white border border-slate-200 rounded-xl space-y-2.5 shadow-2xs">
                    <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto">
                      <Send className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-900">No chat found for "{searchQuery}"</p>
                      <p className="text-[11px] text-slate-500">You can start a new encrypted WhatsApp conversation directly.</p>
                    </div>
                    <button
                      onClick={handleStartChatFromSearch}
                      className="btn-primary text-xs w-full py-2 px-3 flex items-center justify-center gap-1.5"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Start Chat with {searchQuery}</span>
                    </button>
                  </div>
                ) : (
                  <div className="p-8 text-center text-xs text-slate-400 space-y-2">
                    <p>No conversations found.</p>
                    <button
                      onClick={() => setIsNewChatOpen(true)}
                      className="text-emerald-600 font-bold hover:underline"
                    >
                      + Start a new chat
                    </button>
                  </div>
                )}
              </div>
            ) : (
              filteredConversations.map((conv: Conversation) => {
                const isActive = conv.id === activeConversationId;
                const contactTitle =
                  conv.contact?.first_name ||
                  conv.contact?.phone_e164 ||
                  "WhatsApp Contact";
                const isMeta = conv.channel_type === "meta_official";

                return (
                  <button
                    key={conv.id}
                    onClick={() => {
                      setActiveConversationId(conv.id);
                      if (conv.unread_count > 0) {
                        markConversationRead(token, conv.id);
                      }
                    }}
                    className={`w-full p-3 text-left flex items-start gap-3 transition-colors ${
                      isActive ? "bg-emerald-50/70 border-l-4 border-emerald-600" : "hover:bg-slate-100/70"
                    }`}
                  >
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-700 text-xs shrink-0">
                        {contactTitle.slice(0, 2).toUpperCase()}
                      </div>
                      <span className="absolute -bottom-0.5 -right-0.5 p-0.5 rounded-full bg-white shadow-2xs">
                        {isMeta ? (
                          <ShieldCheck className="w-3 h-3 text-blue-600" />
                        ) : (
                          <QrCode className="w-3 h-3 text-emerald-600" />
                        )}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline mb-0.5">
                        <span className="font-bold text-slate-900 text-xs truncate max-w-[140px]">
                          {contactTitle}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono">
                          {new Date(conv.last_message_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>

                      <div className="flex justify-between items-center">
                        <p className="text-xs text-slate-500 truncate max-w-[150px]">
                          {conv.last_message_preview || "No messages yet"}
                        </p>
                        {conv.unread_count > 0 && (
                          <span className="px-1.5 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Center Column: Active Chat Transcript (5 Cols) */}
        <div className="col-span-12 md:col-span-8 lg:col-span-5 flex flex-col h-full min-h-0 bg-white">
          {activeConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-3 border-b border-slate-200 flex items-center justify-between bg-white">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-800 font-bold flex items-center justify-center text-xs">
                    {(activeConversation.contact?.first_name || activeConversation.contact?.phone_e164 || "WA").slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-xs flex items-center gap-1.5">
                      {activeConversation.contact?.first_name || activeConversation.contact?.phone_e164 || "WhatsApp Contact"}
                      {activeConversation.channel_type === "meta_official" && (
                        <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-semibold border border-blue-200">
                          Meta Verified
                        </span>
                      )}
                    </h3>
                    <p className="text-[11px] font-mono text-slate-500">
                      {activeConversation.contact?.phone_e164 || "Active WhatsApp Session"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="status-pill status-pill--connected text-[10px]">
                    Live WebSocket E2EE
                  </span>
                </div>
              </div>

              {/* Message Transcript */}
              <div ref={messagesContainerRef} className="flex-1 p-4 overflow-y-auto space-y-3 bg-slate-50/40">

                {messagesQuery.isLoading ? (
                  <div className="text-center py-16 text-xs text-slate-400 flex flex-col items-center justify-center gap-2">
                    <RefreshCw className="w-5 h-5 text-emerald-600 animate-spin" />
                    <span>Loading message history...</span>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-10 text-xs text-slate-400 space-y-1">
                    <p>No messages in this chat yet.</p>
                    <p className="text-[11px]">Type below to send an encrypted WhatsApp message.</p>
                  </div>
                ) : (
                  messages.map((msg: Message) => {
                    const isOutbound = msg.direction === "outbound";

                    return (
                      <div
                        key={msg.id}
                        className={`flex flex-col ${isOutbound ? "items-end" : "items-start"}`}
                      >
                        <div
                          className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-xs shadow-2xs ${
                            isOutbound
                              ? "bg-emerald-600 text-white rounded-br-xs"
                              : "bg-white text-slate-900 border border-slate-200 rounded-bl-xs"
                          }`}
                        >
                          <p className="whitespace-pre-wrap leading-relaxed">
                            {msg.content?.text || JSON.stringify(msg.content)}
                          </p>

                          <div
                            className={`flex items-center justify-end gap-1 mt-1 text-[10px] ${
                              isOutbound ? "text-emerald-100" : "text-slate-400"
                            }`}
                          >
                            <span className="font-mono">
                              {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </span>
                            {isOutbound && (
                              <span title={`Status: ${msg.status}`}>
                                {msg.status === "read" ? (
                                  <CheckCheck className="w-3.5 h-3.5 text-cyan-300 drop-shadow-xs" />
                                ) : msg.status === "delivered" ? (
                                  <CheckCheck className="w-3.5 h-3.5 text-emerald-200" />
                                ) : msg.status === "sent" ? (
                                  <Check className="w-3.5 h-3.5 text-emerald-200/80" />
                                ) : msg.status === "failed" ? (
                                  <AlertCircle className="w-3.5 h-3.5 text-rose-300" />
                                ) : (
                                  <Clock className="w-3 h-3 text-emerald-200/70" />
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Composer */}
              <div className="p-3 border-t border-slate-200 bg-white relative">
                {/* Canned Replies Popup */}
                {showCannedReplies && (
                  <div className="absolute bottom-full left-3 right-3 mb-2 bg-white border border-slate-200 rounded-xl shadow-lg p-2 max-h-48 overflow-y-auto space-y-1">
                    <div className="text-[10px] font-bold uppercase text-slate-400 px-2 py-1">
                      Quick Canned Replies
                    </div>
                    {cannedReplies.map((reply) => (
                      <button
                        key={reply.id}
                        onClick={() => insertCannedReply(reply)}
                        className="w-full text-left p-2 rounded-lg hover:bg-slate-50 flex items-center justify-between text-xs"
                      >
                        <div>
                          <span className="font-bold text-slate-800">{reply.title}</span>
                          <p className="text-slate-500 text-[11px] truncate max-w-sm">{reply.content}</p>
                        </div>
                        <span className="font-mono text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                          /{reply.shortcut}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                <form onSubmit={handleSend} className="space-y-2">
                  <div className="flex items-center justify-between pb-1">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setShowCannedReplies(!showCannedReplies)}
                        className="text-xs text-slate-600 hover:text-emerald-600 flex items-center gap-1 px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 transition-colors font-medium"
                      >
                        <Zap className="w-3 h-3 text-amber-500" />
                        <span>Quick Reply</span>
                      </button>
                      <button
                        type="button"
                        className="p-1 text-slate-400 hover:text-slate-600 rounded"
                      >
                        <Paperclip className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Active Sending Channel Pill */}
                    <div className="flex items-center gap-1 text-[11px] text-slate-500">
                      <span className="font-semibold text-slate-400">Sending via:</span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-700 font-mono text-[10px]">
                        {activeQR ? (
                          <>
                            <Smartphone className="w-3 h-3 text-emerald-600" />
                            <span>{activeQR.name} ({activeQR.phone_number ? `+${activeQR.phone_number}` : "QR"})</span>
                          </>
                        ) : activeMeta ? (
                          <>
                            <ShieldCheck className="w-3 h-3 text-blue-600" />
                            <span>{activeMeta.name}</span>
                          </>
                        ) : (
                          <>
                            <Radio className="w-3 h-3 text-emerald-600" />
                            <span>Auto (Session Channel)</span>
                          </>
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Type a WhatsApp message..."
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      className="flex-1 px-3.5 py-2 rounded-lg border border-slate-300 text-xs focus:outline-none focus:border-emerald-600 shadow-2xs"
                    />
                    <button
                      type="submit"
                      disabled={!messageInput.trim() || sendMutation.isPending}
                      className="btn-primary py-2 px-3.5 text-xs flex items-center gap-1.5 shadow-2xs"
                    >
                      <Send className="w-3.5 h-3.5" />
                      <span>{sendMutation.isPending ? "Sending..." : "Send"}</span>
                    </button>
                  </div>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 text-xs space-y-2">
              <Radio className="w-10 h-10 text-slate-300" />
              <p>Select a chat from the left or start a new conversation.</p>
            </div>
          )}
        </div>

        {/* Right Column: Contact Metadata & Notes Drawer (3 Cols) */}
        <div className="hidden lg:flex lg:col-span-3 border-l border-slate-200 flex-col h-full min-h-0 bg-slate-50/50 p-4 space-y-4 overflow-y-auto">
          {activeConversation ? (

            <>
              <div className="text-center space-y-2 pb-3 border-b border-slate-200">
                <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-800 font-black text-base flex items-center justify-center mx-auto shadow-2xs">
                  {(activeConversation.contact?.first_name || activeConversation.contact?.phone_e164 || "WA").slice(0, 2).toUpperCase()}
                </div>
                <h4 className="font-bold text-slate-900 text-xs">
                  {activeConversation.contact?.first_name || "WhatsApp User"}
                </h4>
                <p className="font-mono text-[11px] text-slate-500">
                  {activeConversation.contact?.phone_e164 || "—"}
                </p>
              </div>

              <div className="space-y-3 text-xs">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Channel Protocol
                  </label>
                  <div className="font-semibold text-slate-800 mt-0.5">
                    {activeConversation.channel_type === "meta_official"
                      ? "Official Meta Cloud API (WABA)"
                      : "Multi-Device Direct QR Node"}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <TagIcon className="w-3 h-3" /> Assigned Tags
                  </label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold">
                      VIP Customer
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-[10px] font-semibold">
                      Lead
                    </span>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Conversation Status
                  </label>
                  <div className="mt-1">
                    <span className="status-pill status-pill--connected capitalize">
                      {activeConversation.status}
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center text-xs text-slate-400 mt-20">
              No conversation active
            </div>
          )}
        </div>
      </div>

      {/* New Chat Modal */}
      {isNewChatOpen && (
        <div className="modal-backdrop" onClick={() => setIsNewChatOpen(false)}>
          <div className="modal-card max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <User className="w-4 h-4 text-emerald-600" />
                Start New WhatsApp Chat
              </h3>
              <button
                onClick={() => setIsNewChatOpen(false)}
                className="text-slate-400 hover:text-slate-700 text-xs font-bold"
              >
                ✕
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (newChatPhone.trim() && !newChatMutation.isPending) {
                  newChatMutation.mutate();
                }
              }}
              className="space-y-3"
            >
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Recipient Phone Number *
                </label>
                <div className="flex items-center gap-2">
                  <select
                    value={selectedCountryCode}
                    onChange={(e) => setSelectedCountryCode(e.target.value)}
                    className="modern-input font-mono text-xs font-semibold py-2 shrink-0 w-36"
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.code} ({c.country})
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="e.g. 7460169503"
                    value={newChatPhone}
                    onChange={(e) => setNewChatPhone(e.target.value)}
                    className="modern-input w-full font-mono text-xs py-2"
                    autoFocus
                  />
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  Destination: <span className="font-mono text-slate-700 font-bold">{formatPhoneWithCountryCode(newChatPhone, selectedCountryCode) || `${selectedCountryCode}...`}</span>
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Contact Display Name (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Acme Corp Sales Lead"
                  value={newChatName}
                  onChange={(e) => setNewChatName(e.target.value)}
                  className="modern-input w-full text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Sender Channel Type *
                </label>
                <select
                  value={newChatChannel}
                  onChange={(e) => setNewChatChannel(e.target.value as any)}
                  className="modern-input w-full text-xs"
                >
                  <option value="qr_whatsapp">Direct QR Multi-Device WhatsApp Node</option>
                  <option value="meta_official">Official Meta Cloud API (WABA)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Initial Message (Optional)
                </label>
                <textarea
                  placeholder="Hello! How can we assist you today?"
                  value={newChatInitialMessage}
                  onChange={(e) => setNewChatInitialMessage(e.target.value)}
                  className="modern-input w-full text-xs h-20"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="btn-secondary text-xs"
                  onClick={() => setIsNewChatOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary text-xs"
                  disabled={!newChatPhone.trim() || newChatMutation.isPending}
                >
                  {newChatMutation.isPending ? "Creating..." : "Start Chat"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
